import _ from 'underscore';
import kendo from '@bingads-webui/kendo/kendo.core';
import { getSessionStorage } from '@bingads-webui/storage';
import { FilterStorageFactory } from './filter-storage-factory';

export const WaterfallFilterType = {
  CampaignType: 0,
  CampaignStatus: 1,
  AdGroupStatus: 2,
  DateFilter: 3,
  BudgetCenterMonthFilter: 4,
};

const waterfallFilterTypeMapping = {
  [WaterfallFilterType.CampaignType]: 'permanentWaterfallFilter',
  [WaterfallFilterType.CampaignStatus]: 'permanentWaterfallFilter',
  [WaterfallFilterType.AdGroupStatus]: 'permanentWaterfallFilter',
  [WaterfallFilterType.DateFilter]: 'permanentWaterfallFilter',
  [WaterfallFilterType.BudgetCenterMonthFilter]: 'sessionQuickFilter',
};

const waterfallFilterNames = {
  [WaterfallFilterType.CampaignType]: 'New.CampaignType',
  [WaterfallFilterType.CampaignStatus]: 'New.CampaignStatus',
  [WaterfallFilterType.AdGroupStatus]: 'New.AdGroupStatus',
  // Keep the same key for date filter, so we can carry over
  // date filter between previous and new UI.
  [WaterfallFilterType.DateFilter]: 'dateFilter',
  [WaterfallFilterType.BudgetCenterMonthFilter]: 'BudgetCenterMonthFilter',
};

export class WaterfallFilterStorage {
  /**
   * Create wrapper of permanent storage for waterfall filters
   * @param {Object} preferenceService- Preference service instance
   */
  constructor({
    preferenceService,
    sessionStore,
  }) {
    this.waterfallFilterStorages = {};

    _.each(waterfallFilterNames, (value, key) => {
      const filterStorage = new FilterStorageFactory({
        sessionStore: sessionStore || getSessionStorage(),
        preferenceService,
        featureName: value,
      }).create(waterfallFilterTypeMapping[key]);

      this.waterfallFilterStorages = { ...this.waterfallFilterStorages, [key]: filterStorage };
    });
  }

  /**
   * Get waterfall filter object array
   * @param {Array.<WaterfallFilterType>} filterTypes - Array of waterfall filter types
   * @return {Array.<object>} - Array of Waterfall filter object
   */
  getFilters(filterTypes) {
    return filterTypes.map((type) => {
      const storage = this.waterfallFilterStorages[type];
      return storage.getFilterInSync();
    });
  }

  /**
   * Save a waterfall filter by its type
   * @param {WaterfallFilterType} filterType - Waterfall filter type
   * @param {Object} filter - Waterfall filter object
   * @return {void}
   */
  save(filterType, filter) {
    const storage = this.waterfallFilterStorages[filterType];
    storage.save(filter);
  }
}

/**
 * helper function to format waterfall date filte objct
 * @param {Object} dateFilter - date filter storage
 * @param {string} format - used to parse date
 * @return {Object} cooked value
 */
export const dateFilterProcessor = (dateFilter, format) => {
  if (!dateFilter) return null;
  const newFilter = dateFilter;
  if (newFilter.compared) {
    newFilter.compared.start = kendo.parseDate(
      newFilter.compared.formattedStart,
      format
    );
    newFilter.compared.end = kendo.parseDate(
      newFilter.compared.formattedEnd,
      format
    );
  }
  return _.defaults({}, newFilter, {
    start: newFilter.RangeStartDate && kendo.parseDate(
      newFilter.RangeStartDate,
      format
    ),
    end: newFilter.RangeEndDate && kendo.parseDate(
      newFilter.RangeEndDate,
      format
    ),
    grain: newFilter.RangeId,
  });
};

/**
 * helper function to format waterfall date filte objct
 * we need serverContext to get date format, and here after
 * parseDate we get a date that is using local timeZone to
 * keep consistent with old UI kendo.toString timezone
 * @param {Array} dateFilterStorage - date filter storage
 * @param {Object} serverContext - used to parse date
 * @return {Object} cooked value
 */
export function waterfallDateFilterProcessor(dateFilterStorage, serverContext) {
  const dateFilter = dateFilterStorage && dateFilterStorage[0] && dateFilterStorage[0].filter;
  const format = serverContext.CurrentUser.CultureDateTimeFormat.ShortDatePattern;
  return dateFilterProcessor(dateFilter, format);
}
